import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import SocialAuthButtons from '../components/common/SocialAuthButtons';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Cultural photograph for the auth split-screen's left panel. Leave empty for the
// branded gradient fallback. To add one: `import authHero from '../assets/auth-hero.jpg'`
// then set AUTH_HERO_IMAGE = authHero (or a hosted URL). The scrim keeps the
// overlaid copy legible on any image.
const AUTH_HERO_IMAGE = '';

const getInputStyle = (VB) => ({
  width: '100%',
  background: VB.canvas,
  border: `1px solid ${VB.borderDark}`,
  color: VB.snowPrimary,
  borderRadius: 12,
  padding: '14px 16px',
  outline: 'none',
  boxSizing: 'border-box',
});

const getLabelStyle = (VB) => ({
  fontSize: 12,
  color: VB.snowMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 6,
  display: 'block',
  fontWeight: 600,
});

const maskEmail = (email) => {
  const [local, domain] = String(email || '').toLowerCase().split('@');
  if (!local || !domain) return '';
  return `${local[0]}***@${domain}`;
};

export default function AuthPage() {
  const { VB } = useTheme();
  const inputStyle = useMemo(() => getInputStyle(VB), [VB]);
  const labelStyle = useMemo(() => getLabelStyle(VB), [VB]);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = searchParams.get('tab') === 'login' || location.pathname === '/login' ? 'login' : 'signup';
  const [tab, setTab] = useState(initialTab);
  const [step, setStep] = useState('entry'); // entry | otp | profile

  const [signupEmail, setSignupEmail] = useState('');
  // Pre-fill from the URL so a launch/waitlist email (?email=&name=) drops people
  // straight into signup without retyping what they already gave us.
  const [signupEmailInput, setSignupEmailInput] = useState(searchParams.get('email') || '');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [countdown, setCountdown] = useState(30);
  const otpRefs = useRef([]);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  // Login 2FA (admin/support or TOTP-enabled users): backend replies 401 { requires2FA }
  // to /auth/login, then the emailed 6-digit code is submitted to /auth/verify-2fa-code.
  const [login2fa, setLogin2fa] = useState(false);
  const [login2faCode, setLogin2faCode] = useState('');
  const [profile, setProfile] = useState({ name: searchParams.get('name') || '', phone: '', mm: '', dd: '', yyyy: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoSubmittingOtp, setAutoSubmittingOtp] = useState(false);

  const otpCode = useMemo(() => otp.join(''), [otp]);

  useEffect(() => {
    const nextTab = searchParams.get('tab') === 'login' || location.pathname === '/login' ? 'login' : 'signup';
    setTab(nextTab);
  }, [searchParams, location.pathname]);

  useEffect(() => {
    if (step !== 'otp' || countdown <= 0) return undefined;
    const t = setTimeout(() => setCountdown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [step, countdown]);

  const goAfterAuth = useCallback((authUser) => {
    // Only allow relative paths — reject external and protocol-relative URLs.
    //
    // Checking that the second character is not `/` is not enough on its own.
    // A browser reads `/\evil.com` as protocol-relative too, so a login link
    // carrying ?redirect=/\evil.com sent the user off Vibana the moment they
    // signed in, with their session freshly created — the phishing setup this
    // check exists to prevent. Browsers also strip tabs, newlines and other
    // control characters out of a URL before resolving it, so `/<tab>/evil.com`
    // collapses to `//evil.com` after passing any check made on the raw string.
    // Strip those first, then require a single leading slash.
    const raw = searchParams.get('redirect') || '';
    const redirectTo = raw.replace(/[\u0000-\u0020\u007F]/g, '');
    if (redirectTo && /^\/[^/\\]/.test(redirectTo)) {
      navigate(redirectTo);
      return;
    }
    if (authUser?.role === 'support' || authUser?.role === 'admin') {
      navigate('/support');
      return;
    }
    if (authUser?.role === 'organizer') {
      navigate('/dashboard');
      return;
    }
    navigate('/');
  }, [navigate, searchParams]);

  const setTabAndQuery = (nextTab) => {
    setTab(nextTab);
    setError('');
    setStep('entry');
    setOtp(Array(6).fill(''));
    setSearchParams({ tab: nextTab === 'login' ? 'login' : 'signup' });
  };

  const handleSendCode = async () => {
    const email = String(signupEmailInput || '').trim().toLowerCase();
    if (!emailRegex.test(email)) {
      setError('Enter a valid email');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/otp/send', { email });
      if (res.data?.accountExists) {
        // The signup code only creates NEW accounts. An existing email is sent
        // to the password login (or told to use their provider / set-password link).
        let msg;
        if (res.data.requiresPasswordSetup) {
          msg = 'You already have an account from a previous order. Check your email for a link to set your password, then log in.';
        } else if (res.data.provider) {
          msg = `You already have an account. Continue with ${res.data.provider === 'google' ? 'Google' : 'Apple'} to sign in.`;
        } else {
          msg = 'You already have an account — please log in with your password.';
        }
        setLoginForm({ email, password: '' });
        setTab('login');
        setStep('entry');
        setSearchParams({ tab: 'login' });
        setError(msg);
        return;
      }
      setSignupEmail(email);
      setMaskedEmail(res.data?.maskedEmail || maskEmail(email));
      setOtp(Array(6).fill(''));
      setCountdown(30);
      setStep('otp');
      setTimeout(() => otpRefs.current[0]?.focus(), 0);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = useCallback(async () => {
    if (otpCode.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/otp/verify', { email: signupEmail, code: otpCode });
      if (res.data.exists) {
        // Never log in by code — an existing account uses the password login.
        setLoginForm({ email: signupEmail, password: '' });
        setTab('login');
        setStep('entry');
        setSearchParams({ tab: 'login' });
        setError('You already have an account — please log in with your password.');
        return;
      }
      setStep('profile');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setLoading(false);
    }
  }, [otpCode, signupEmail, setSearchParams]);

  useEffect(() => {
    if (step !== 'otp' || otpCode.length !== 6 || autoSubmittingOtp || loading) return;
    setAutoSubmittingOtp(true);
    handleVerifyOtp().finally(() => setAutoSubmittingOtp(false));
  }, [otpCode, step, autoSubmittingOtp, loading, handleVerifyOtp]);

  const handleLogin = async () => {
    const email = loginForm.email.trim().toLowerCase();
    if (!emailRegex.test(email) || !loginForm.password) {
      setError('Enter your email and password');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password: loginForm.password });
      loginWithToken(res.data.token, res.data.user);
      goAfterAuth(res.data.user);
    } catch (err) {
      // Admin/support or TOTP users: backend sends a 6-digit email code and asks for it.
      if (err.response?.status === 401 && err.response?.data?.requires2FA) {
        setLogin2faCode('');
        setLogin2fa(true);
        setError('');
        return;
      }
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLogin2fa = async () => {
    const email = loginForm.email.trim().toLowerCase();
    const code = login2faCode.trim();
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your email');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-2fa-code', { email, code });
      loginWithToken(res.data.token, res.data.user);
      goAfterAuth(res.data.user);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSignup = async () => {
    if (!profile.password || profile.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (profile.password !== profile.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const dob = `${profile.yyyy}-${profile.mm}-${profile.dd}`;
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/otp/complete-signup', {
        email: signupEmail,
        name: profile.name,
        phone: profile.phone || null,
        dob,
        password: profile.password,
        confirmPassword: profile.confirmPassword,
      });
      loginWithToken(res.data.token, res.data.user);
      navigate('/onboarding');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to complete sign up');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    if (countdown > 0) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/otp/send', { email: signupEmail });
      setCountdown(30);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const onOtpChange = (index, rawValue) => {
    const digits = String(rawValue).replace(/\D/g, '');
    if (!digits) {
      const next = [...otp];
      next[index] = '';
      setOtp(next);
      return;
    }

    const next = [...otp];
    if (digits.length > 1) {
      if (digits.length === 6) {
        setOtp(digits.slice(0, 6).split(''));
        otpRefs.current[5]?.focus();
        return;
      }
      for (let i = 0; i < digits.length && index + i < 6; i += 1) {
        next[index + i] = digits[i];
      }
      setOtp(next);
      const focusIndex = Math.min(5, index + digits.length);
      otpRefs.current[focusIndex]?.focus();
      return;
    }

    next[index] = digits;
    setOtp(next);
    if (index < 5) otpRefs.current[index + 1]?.focus();
  };

  const onOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const filled = Array(6).fill('');
    pasted.split('').forEach((d, i) => { filled[i] = d; });
    setOtp(filled);
    otpRefs.current[Math.min(5, pasted.length - 1)]?.focus();
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 80px)', display: 'flex', background: VB.void }}>
      <style>{`.auth-hero-panel{display:none} @media (min-width: 900px){.auth-hero-panel{display:block}}`}</style>
      {/* Left — editorial imagery panel (desktop only) */}
      <div className="auth-hero-panel" aria-hidden="true" style={{ flex: '1 1 46%', position: 'relative', overflow: 'hidden', background: 'linear-gradient(160deg, #1A0E0B 0%, #0A0908 58%)' }}>
        {AUTH_HERO_IMAGE && (
          <>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${AUTH_HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(10,9,8,0.30) 0%, rgba(10,9,8,0.55) 58%, rgba(10,9,8,0.92) 100%)' }} />
          </>
        )}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(70% 55% at 28% 22%, rgba(233,162,59,0.20), transparent 60%), radial-gradient(60% 50% at 82% 86%, rgba(230,57,70,0.16), transparent 55%)' }} />
        <div style={{ position: 'absolute', left: 48, right: 48, bottom: 56 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 18, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: VB.gold }}>
            <span style={{ width: 26, height: 2, borderRadius: 2, background: 'var(--vb-seam, linear-gradient(90deg,#B8860B,#E9A23B,#F3D27A))' }} />
            The diaspora, in one place
          </div>
          <div style={{ fontFamily: 'var(--font-display, Georgia, serif)', fontWeight: 600, fontSize: 40, lineHeight: 1.05, letterSpacing: '-0.03em', color: '#fff', maxWidth: '14ch' }}>
            Discover African <em style={{ fontStyle: 'italic', color: '#F3D27A', fontWeight: 500 }}>culture</em> events near you.
          </div>
          <div style={{ marginTop: 18, fontSize: 14.5, color: 'rgba(251,244,230,0.72)', maxWidth: '34ch', lineHeight: 1.55 }}>
            Music, food, art and community — curated by organizers across the US &amp; Canada.
          </div>
        </div>
      </div>
      {/* Right — the auth form (unchanged) */}
      <div style={{ flex: '1 1 54%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', background: `radial-gradient(58% 44% at 50% 0%, rgba(233,162,59,0.10), transparent 62%), radial-gradient(52% 42% at 50% 100%, rgba(230,57,70,0.07), transparent 60%), ${VB.void}` }}>
      <div style={{ maxWidth: 448, width: '100%', border: `1px solid ${VB.borderDark}`, borderRadius: 20, background: VB.darkCard, padding: 32, position: 'relative', overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }}>
        <div aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--vb-seam, linear-gradient(90deg,#B8860B,#E9A23B,#F3D27A))' }} />
        {step !== 'entry' && (
          <button
            type="button"
            aria-label="Back"
            onClick={() => {
              setError('');
              if (step === 'otp') setStep('entry');
              if (step === 'profile') setStep('otp');
            }}
            style={{ border: 'none', background: 'transparent', color: VB.snowPrimary, cursor: 'pointer', position: 'absolute', top: 18, left: 16, padding: 4, display: 'flex' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
          </button>
        )}

        <div style={{ textAlign: 'center', fontSize: 30, fontWeight: 500, color: VB.snowPrimary, marginBottom: 12, marginTop: 4, letterSpacing: '-0.02em', fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto' }}>
          vibana
        </div>

        {/* Step indicator — only shown for signup flow */}
        {tab === 'signup' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
            {['entry', 'otp', 'profile'].map((s, idx) => {
              const stepIndex = ['entry', 'otp', 'profile'].indexOf(step);
              const isActive = idx === stepIndex;
              const isDone = idx < stepIndex;
              return (
                <React.Fragment key={s}>
                  {idx > 0 && (
                    <div style={{ width: 24, height: 1, background: isDone ? VB.red : VB.borderDark }} />
                  )}
                  <div style={{
                    width: isActive ? 10 : 8,
                    height: isActive ? 10 : 8,
                    borderRadius: '50%',
                    background: isActive ? VB.red : isDone ? VB.red : VB.borderDark,
                    opacity: isDone ? 0.55 : 1,
                    transition: 'all 0.2s',
                  }} />
                </React.Fragment>
              );
            })}
            <span style={{ fontSize: 11, color: VB.snowMuted, marginLeft: 4 }}>
              Step {['entry', 'otp', 'profile'].indexOf(step) + 1} of 3
            </span>
          </div>
        )}

        {step === 'entry' && (
          <>
            <h1 style={{ color: VB.snowPrimary, margin: 0, fontSize: 32, fontWeight: 600, fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', textAlign: 'center', letterSpacing: '-0.6px' }}>Welcome to Vibana</h1>

            <div style={{ marginTop: 16, display: 'flex', borderBottom: `1px solid ${VB.borderDark}` }}>
              <button onClick={() => setTabAndQuery('signup')} style={{ flex: 1, backgroundColor: 'transparent', backgroundImage: tab === 'signup' ? 'var(--vb-seam)' : 'none', backgroundRepeat: 'no-repeat', backgroundSize: '100% 2.5px', backgroundPosition: 'center bottom', border: 'none', color: tab === 'signup' ? VB.snowPrimary : VB.snowMuted, padding: '11px 0', fontWeight: 700, letterSpacing: '0.01em', cursor: 'pointer', transition: 'color 200ms ease' }}>Sign up</button>
              <button onClick={() => setTabAndQuery('login')} style={{ flex: 1, backgroundColor: 'transparent', backgroundImage: tab === 'login' ? 'var(--vb-seam)' : 'none', backgroundRepeat: 'no-repeat', backgroundSize: '100% 2.5px', backgroundPosition: 'center bottom', border: 'none', color: tab === 'login' ? VB.snowPrimary : VB.snowMuted, padding: '11px 0', fontWeight: 700, letterSpacing: '0.01em', cursor: 'pointer', transition: 'color 200ms ease' }}>Log in</button>
            </div>

            <div style={{ marginTop: 18 }}>
              <SocialAuthButtons onError={setError} />
            </div>

            {tab === 'signup' ? (
              <div style={{ marginTop: 16 }}>
                <label htmlFor="signup-email" style={labelStyle}>Email</label>
                <input id="signup-email" type="email" aria-required="true" autoComplete="email" value={signupEmailInput} onChange={(e) => setSignupEmailInput(e.target.value)} placeholder="you@example.com" style={inputStyle} />
                <button onClick={handleSendCode} disabled={loading} style={{ width: '100%', marginTop: 14, padding: '14px 20px', borderRadius: 12, background: VB.red, color: VB.white, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 15, boxShadow: '0 6px 20px rgba(230,57,70,0.24)' }}>
                  {loading ? 'Sending...' : 'Continue'}
                </button>
              </div>
            ) : login2fa ? (
              <div style={{ marginTop: 16 }}>
                <p style={{ color: VB.snowMuted, margin: '0 0 14px', fontSize: 14 }}>
                  Two-factor authentication is on for this account. Enter the 6-digit code we emailed to {loginForm.email}.
                </p>
                <label htmlFor="login-2fa-code" style={labelStyle}>Verification code</label>
                <input
                  id="login-2fa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-required="true"
                  maxLength={6}
                  value={login2faCode}
                  onChange={(e) => setLogin2faCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyLogin2fa(); }}
                  placeholder="123456"
                  style={{ ...inputStyle, letterSpacing: 6, textAlign: 'center', fontSize: 20 }}
                />
                <button onClick={handleVerifyLogin2fa} disabled={loading} style={{ width: '100%', marginTop: 14, padding: '14px 20px', borderRadius: 12, background: VB.red, color: VB.white, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 15, boxShadow: '0 6px 20px rgba(230,57,70,0.24)' }}>
                  {loading ? 'Verifying...' : 'Verify & sign in'}
                </button>
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                  <button onClick={() => { setLogin2fa(false); setLogin2faCode(''); setError(''); }} style={{ border: 'none', background: 'none', color: VB.snowMuted, cursor: 'pointer', fontSize: 13 }}>← Back to login</button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 16 }}>
                <label htmlFor="login-email" style={labelStyle}>Email</label>
                <input id="login-email" type="email" aria-required="true" autoComplete="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="you@example.com" style={{ ...inputStyle, marginBottom: 10 }} />
                <label htmlFor="login-password" style={labelStyle}>Password</label>
                <input id="login-password" type="password" aria-required="true" autoComplete="current-password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="••••••••" style={inputStyle} />
                <button onClick={handleLogin} disabled={loading} style={{ width: '100%', marginTop: 14, padding: '14px 20px', borderRadius: 12, background: VB.red, color: VB.white, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 15, boxShadow: '0 6px 20px rgba(230,57,70,0.24)' }}>
                  {loading ? 'Logging in...' : 'Log in'}
                </button>
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                  <Link to="/forgot-password" style={{ color: VB.snowMuted, fontSize: 13 }}>Forgot password?</Link>
                </div>
              </div>
            )}

            <p style={{ textAlign: 'center', marginTop: 18, color: VB.snowMuted, fontSize: 12 }}>
              By continuing you agree to our <Link to="/terms" style={{ color: VB.snowMuted }}>Terms</Link> &amp; <Link to="/privacy" style={{ color: VB.snowMuted }}>Privacy Policy</Link>
            </p>
          </>
        )}

        {step === 'otp' && (
          <>
            <h1 style={{ color: VB.snowPrimary, margin: 0, fontSize: 27, fontWeight: 600, fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', marginTop: 12, letterSpacing: '-0.4px' }}>Check your email</h1>
            <p style={{ color: VB.snowMuted, margin: '8px 0 16px', fontSize: 14 }}>We sent a 6-digit code to {maskedEmail}</p>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }} onPaste={onOtpPaste}>
              {otp.map((digit, i) => (
                <React.Fragment key={i}>
                  <input
                    ref={(el) => { otpRefs.current[i] = el; }}
                    value={digit}
                    onChange={(e) => onOtpChange(i, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !otp[i] && i > 0) {
                        otpRefs.current[i - 1]?.focus();
                      }
                    }}
                    aria-label={`Verification code digit ${i + 1} of 6`}
                    style={{ width: 48, height: 56, textAlign: 'center', ...inputStyle, padding: 0, fontSize: 24, fontWeight: 700 }}
                    inputMode="numeric"
                    maxLength={1}
                  />
                  {i === 2 && <div style={{ width: 16 }} />}
                </React.Fragment>
              ))}
            </div>
            {countdown > 0 ? (
              <div style={{ color: VB.snowMuted, fontSize: 12, marginBottom: 10 }}>Resend in 0:{String(countdown).padStart(2, '0')}</div>
            ) : (
              <button onClick={resendCode} style={{ border: 'none', background: 'none', color: VB.snowPrimary, cursor: 'pointer', padding: 0, marginBottom: 10, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 4 }}>Resend code</button>
            )}
            <button onClick={handleVerifyOtp} disabled={loading || otpCode.length !== 6} style={{ width: '100%', padding: '14px 20px', borderRadius: 12, background: VB.red, color: VB.white, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 15, boxShadow: '0 6px 20px rgba(230,57,70,0.24)', opacity: (loading || otpCode.length !== 6) ? 0.5 : 1 }}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </>
        )}

        {step === 'profile' && (
          <>
            <h1 style={{ color: VB.snowPrimary, margin: 0, fontSize: 27, fontWeight: 600, fontFamily: 'var(--font-display)', fontOpticalSizing: 'auto', marginTop: 12, letterSpacing: '-0.4px' }}>Tell us about yourself</h1>
            <div style={{ marginTop: 14 }}>
              <label htmlFor="profile-name" style={labelStyle}>Full name</label>
              <input id="profile-name" type="text" aria-required="true" autoComplete="name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Full name" style={{ ...inputStyle, marginBottom: 10 }} />

              <label htmlFor="profile-phone" style={labelStyle}>Optional — for account security</label>
              <input id="profile-phone" type="tel" autoComplete="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Phone (optional)" style={{ ...inputStyle, marginBottom: 10 }} />

              <label style={labelStyle}>Birthday</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: 8, marginBottom: 12 }}>
                <input aria-label="Birth month" inputMode="numeric" value={profile.mm} onChange={(e) => setProfile({ ...profile, mm: e.target.value.replace(/\D/g, '').slice(0, 2) })} placeholder="MM" style={inputStyle} />
                <input aria-label="Birth day" inputMode="numeric" value={profile.dd} onChange={(e) => setProfile({ ...profile, dd: e.target.value.replace(/\D/g, '').slice(0, 2) })} placeholder="DD" style={inputStyle} />
                <input aria-label="Birth year" inputMode="numeric" value={profile.yyyy} onChange={(e) => setProfile({ ...profile, yyyy: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="YYYY" style={inputStyle} />
              </div>

              <label htmlFor="profile-password" style={labelStyle}>Password</label>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <input
                  id="profile-password"
                  type={showPassword ? 'text' : 'password'}
                  aria-required="true"
                  autoComplete="new-password"
                  value={profile.password}
                  onChange={(e) => setProfile({ ...profile, password: e.target.value })}
                  placeholder="Min 8 characters"
                  style={{ ...inputStyle, paddingRight: 44 }}
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: VB.snowMuted, fontSize: 18, lineHeight: 1 }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {profile.password.length > 0 && (() => {
                const len = profile.password.length;
                const strength = len < 8 ? 1 : len < 10 ? 2 : len < 12 ? 3 : 4;
                const colors = ['#E63946', '#E9C061', '#EAB308', '#10B981'];
                const labels = ['Too short', 'Weak', 'Fair', 'Strong'];
                return (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      {[1, 2, 3, 4].map((seg) => (
                        <div key={seg} style={{ flex: 1, height: 4, borderRadius: 2, background: seg <= strength ? colors[strength - 1] : 'rgba(0,0,0,0.1)' }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 11, color: colors[strength - 1] }}>{labels[strength - 1]}</span>
                  </div>
                );
              })()}

              <label htmlFor="profile-confirm-password" style={labelStyle}>Confirm Password</label>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <input
                  id="profile-confirm-password"
                  type={showConfirm ? 'text' : 'password'}
                  aria-required="true"
                  autoComplete="new-password"
                  value={profile.confirmPassword}
                  onChange={(e) => setProfile({ ...profile, confirmPassword: e.target.value })}
                  placeholder="Repeat password"
                  style={{ ...inputStyle, paddingRight: 44, borderColor: profile.confirmPassword.length > 0 && profile.confirmPassword !== profile.password ? VB.red : VB.borderDark }}
                />
                <button
                  type="button"
                  aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                  onClick={() => setShowConfirm((v) => !v)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: VB.snowMuted, fontSize: 18, lineHeight: 1 }}
                >
                  {showConfirm ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <button onClick={handleCompleteSignup} disabled={loading} style={{ width: '100%', padding: '14px 20px', borderRadius: 12, background: VB.red, color: VB.white, border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 15, boxShadow: '0 6px 20px rgba(230,57,70,0.24)', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Creating...' : "Let's go"}
            </button>
          </>
        )}

        {!!error && (
          <div style={{ marginTop: 12, background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.3)', borderRadius: 8, color: '#E63946', fontSize: 13, padding: '9px 12px' }}>
            {error}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
